import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  collection,
  addDoc,
  where,
  query,
  deleteDoc,
  deleteField,
  doc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { db, storage } from '../firebase';
import { getDownloadURL, ref } from 'firebase/storage';
import { useUser } from './UserContext';
import { Heading } from './heading';
import { Text } from './text';
import { Button } from './button';
import { Badge } from './badge';
import { Dialog, DialogTitle, DialogBody, DialogActions } from './dialog';
import SearchableListbox from './SearchableListbox';
import FileReceiver from './FileReceiver';
import FileHistoryPopover from './FileHistoryPopover';
import ObligadoSolidarioDialog from './ObligadoSolidarioDialog';
import BienInmuebleDialog from './BienInmuebleDialog';
import { getOsDocs, OS_AFIANZADORA_DOCS } from './ObligadoSolidarioDialog';
import { BIEN_INMUEBLE_DOCS } from './BienInmuebleDialog';
import { REQUIRED_DOCS } from '../hooks/usePulsoData';
import { FianzaView } from './FianzaView';
import { getFianzaStatus } from '../utils/convenioCompliance';
import { ProcessedFileView } from './ProcessedFileView';
import { uploadDocumentFile, reprocessDocumentFile } from '../api/afianzadoraUpload';
import { useClientContracts, useContracts, useAfianzadoras, useClients, useLinks } from '../contexts/DataContext';
import { getEmisorNombre } from '../utils/calculations';
import {
  TrashIcon,
  ArrowUpTrayIcon,
  ShieldCheckIcon,
  ClockIcon,
  LinkIcon,
  XMarkIcon,
  UserIcon,
  BuildingOfficeIcon,
  HomeModernIcon,
  PlusIcon,
  CurrencyDollarIcon,
  ArrowTopRightOnSquareIcon,
  DocumentTextIcon,
  EyeIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/solid';

const FILE_TYPES = [
  { id: 'contratoMultiple', label: 'Contrato Multiple', versionable: true },
  { id: 'revisionBuro', label: 'Revision de Buro', versionable: false },
  { id: 'entrevista', label: 'Entrevista', versionable: true },
];

const STATUS_BADGE = {
  pendiente: { color: 'zinc', label: 'Pendiente' },
  subido: { color: 'blue', label: 'Subido' },
  procesando: { color: 'amber', label: 'Procesando' },
  procesado: { color: 'green', label: 'Procesado' },
};

// Parse composite bien reference: "clientId/bienId"
function parseBienRef(ref) {
  const idx = ref.indexOf('/');
  if (idx === -1) return { clientId: null, bienId: ref };
  return { clientId: ref.slice(0, idx), bienId: ref.slice(idx + 1) };
}

// Helper to count uploaded docs
function countUploaded(files, docList) {
  if (!files) return { done: 0, total: docList.length };
  let done = 0;
  docList.forEach((d) => {
    if (files[d.id] && files[d.id].status !== 'pendiente') done++;
  });
  return { done, total: docList.length };
}

// Format currency (Mexican pesos with commas)
function formatMXN(value) {
  if (!value && value !== 0) return '';
  return '$' + Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 }) + ' MXN';
}

// Format input value with commas as user types
function formatInputWithCommas(raw) {
  const digits = raw.replace(/[^0-9]/g, '');
  if (!digits) return '';
  return Number(digits).toLocaleString('en-US');
}

// Parse comma-formatted string back to number
function parseFormattedNumber(formatted) {
  return parseFloat(formatted.replace(/[^0-9]/g, '')) || 0;
}

const ClientAfianzadoras = ({ clientId, clientData }) => {
  const { user, userData, isOwner } = useUser();
  const { clientsById } = useClients();
  const { linksById } = useLinks();
  const clientContracts = useClientContracts(clientId);
  const { contractsList: allContracts } = useContracts();
  const { afianzadorasList: globalAfianzadoras, afianzadorasById: globalMap, afianzadorasLoading: loadingGlobal } = useAfianzadoras();

  // Client's afianzadoras (real-time)
  const [clientAfianzadoras, setClientAfianzadoras] = useState([]);
  const [loadingClient, setLoadingClient] = useState(true);

  // Client's obligados solidarios (real-time)
  const [obligadosSolidarios, setObligadosSolidarios] = useState([]);

  // Client's bienes inmuebles (real-time)
  const [bienesInmuebles, setBienesInmuebles] = useState([]);

  // Add afianzadora dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedAfianzadoraId, setSelectedAfianzadoraId] = useState(null);
  const [adding, setAdding] = useState(false);

  // Upload dialog
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Delete afianzadora confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // File history popover
  const [historyTarget, setHistoryTarget] = useState(null); // { history, currentFile, label }

  // Linea editing
  const [editingLinea, setEditingLinea] = useState(null); // docId
  const [lineaValue, setLineaValue] = useState('');
  const [savingLinea, setSavingLinea] = useState(false);

  // Vincular OS dialog
  const [showVincularOS, setShowVincularOS] = useState(null); // afianzadora docId
  const [selectedOSToLink, setSelectedOSToLink] = useState(null);

  // Vincular BI dialog
  const [showVincularBI, setShowVincularBI] = useState(null); // afianzadora docId
  const [selectedBIToLink, setSelectedBIToLink] = useState(null);

  // OS dialog
  const [showOSDialog, setShowOSDialog] = useState(false);
  const [osDialogData, setOsDialogData] = useState(null); // null = create, object = view

  // BI dialog
  const [showBIDialog, setShowBIDialog] = useState(false);
  const [biDialogData, setBiDialogData] = useState(null);

  // Delete OS/BI confirmation
  const [showDeleteOSDialog, setShowDeleteOSDialog] = useState(null); // OS id
  const [showDeleteBIDialog, setShowDeleteBIDialog] = useState(null); // BI id
  const [deletingEntity, setDeletingEntity] = useState(false);

  // Per-afianzadora OS file upload
  const [osFileUpload, setOsFileUpload] = useState(null); // { afianzadoraDocId, osId, fileType }
  const [osFileUploadFile, setOsFileUploadFile] = useState(null);
  const [osFileUploading, setOsFileUploading] = useState(false);

  // Fianza view
  const [selectedFianza, setSelectedFianza] = useState(null);
  const [isFianzaViewOpen, setIsFianzaViewOpen] = useState(false);

  // Processed file view (extracted data)
  const [isProcessedViewOpen, setIsProcessedViewOpen] = useState(false);
  const [processedViewData, setProcessedViewData] = useState({});

  // Reprocessing state — tracks which files are currently reprocessing
  const [reprocessingFiles, setReprocessingFiles] = useState(new Set());

  // --- Data fetching ---

  // Real-time listener: client's afianzadoras
  useEffect(() => {
    if (!clientId || !userData?.org) return;
    const q = query(
      collection(db, 'clientes', clientId, 'afianzadoras'),
      where('org', '==', userData.org)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setClientAfianzadoras(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingClient(false);
      },
      (err) => {
        console.error('Error listening to client afianzadoras:', err, { clientId, uid: user?.uid, rol: userData?.rol, org: userData?.org });
        setLoadingClient(false);
      }
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, userData?.org]);

  // Real-time listener: obligados solidarios
  useEffect(() => {
    if (!clientId || !userData?.org) return;
    const q = query(
      collection(db, 'clientes', clientId, 'obligados_solidarios'),
      where('org', '==', userData.org)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setObligadosSolidarios(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => console.error('Error listening to OS:', err, { clientId, uid: user?.uid, rol: userData?.rol, org: userData?.org })
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, userData?.org]);

  // Real-time listener: bienes inmuebles
  useEffect(() => {
    if (!clientId || !userData?.org) return;
    const q = query(
      collection(db, 'clientes', clientId, 'bienes_inmuebles'),
      where('org', '==', userData.org)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setBienesInmuebles(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => console.error('Error listening to bienes:', err, { clientId, uid: user?.uid, rol: userData?.rol, org: userData?.org })
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, userData?.org]);

  // Cross-client bienes from OS-referenced clients
  const [crossClientBienes, setCrossClientBienes] = useState({});

  const osRefClientIds = useMemo(() => {
    const ids = new Set();
    obligadosSolidarios.forEach((os) => {
      if (os.clienteRef && os.clienteRef !== clientId) ids.add(os.clienteRef);
    });
    return [...ids];
  }, [obligadosSolidarios, clientId]);

  useEffect(() => {
    if (osRefClientIds.length === 0 || !userData?.org) {
      setCrossClientBienes({});
      return;
    }
    const unsubs = osRefClientIds.map((refId) =>
      onSnapshot(
        query(
          collection(db, 'clientes', refId, 'bienes_inmuebles'),
          where('org', '==', userData.org)
        ),
        (snap) => {
          setCrossClientBienes((prev) => ({
            ...prev,
            [refId]: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
          }));
        },
        (err) => console.error(`Error listening to cross-client bienes (${refId}):`, err)
      )
    );
    return () => {
      unsubs.forEach((u) => u());
      setCrossClientBienes({});
    };
  }, [osRefClientIds, userData?.org]);

  // Cross-client afianzadoras from OS-referenced clients
  const [crossClientAfianzadoras, setCrossClientAfianzadoras] = useState({});

  useEffect(() => {
    if (osRefClientIds.length === 0 || !userData?.org) {
      setCrossClientAfianzadoras({});
      return;
    }
    const unsubs = osRefClientIds.map((refId) =>
      onSnapshot(
        query(
          collection(db, 'clientes', refId, 'afianzadoras'),
          where('org', '==', userData.org)
        ),
        (snap) => {
          setCrossClientAfianzadoras((prev) => ({
            ...prev,
            [refId]: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
          }));
        },
        (err) => console.error(`Error listening to cross-client afianzadoras (${refId}):`, err)
      )
    );
    return () => {
      unsubs.forEach((u) => u());
      setCrossClientAfianzadoras({});
    };
  }, [osRefClientIds, userData?.org]);

  // --- Memos ---

  const addedIds = useMemo(
    () => new Set(clientAfianzadoras.map((ca) => ca.afianzadoraId)),
    [clientAfianzadoras]
  );

  const addOptions = useMemo(
    () =>
      globalAfianzadoras
        .filter((a) => !addedIds.has(a.id))
        .map((a) => ({ value: a.id, label: a.nombre })),
    [globalAfianzadoras, addedIds]
  );

  const osMap = useMemo(() => {
    const m = {};
    obligadosSolidarios.forEach((os) => { m[os.id] = os; });
    return m;
  }, [obligadosSolidarios]);

  // Combined biMap: "clientId/bienId" → bienDoc with _ownerClientId
  const biMap = useMemo(() => {
    const m = {};
    // Local bienes
    bienesInmuebles.forEach((bi) => {
      const key = `${clientId}/${bi.id}`;
      m[key] = { ...bi, _ownerClientId: clientId };
    });
    // Cross-client bienes
    Object.entries(crossClientBienes).forEach(([refClientId, bienes]) => {
      bienes.forEach((bi) => {
        const key = `${refClientId}/${bi.id}`;
        m[key] = { ...bi, _ownerClientId: refClientId };
      });
    });
    return m;
  }, [bienesInmuebles, crossClientBienes, clientId]);

  // Expediente status for OS-ref clients (excludes 'contrato' — irrelevant for OS)
  const osRefExpedienteStatus = useMemo(() => {
    const osRelevantTotal = REQUIRED_DOCS.filter((d) => d !== 'contrato').length;
    const result = {};
    for (const os of obligadosSolidarios) {
      const refId = os.clienteRef;
      if (!refId || result[refId]) continue;
      const refClient = clientsById[refId];
      if (!refClient) { result[refId] = null; continue; }

      const clientLinks = (refClient.links || []).map((id) => linksById[id]).filter(Boolean);
      if (clientLinks.length === 0) { result[refId] = { uploaded: 0, requested: 0, total: osRelevantTotal, hasExpediente: false }; continue; }

      const uploaded = new Set();
      const requested = new Set();
      for (const link of clientLinks) {
        const files = link.files || {};
        for (const docType of Object.keys(files)) {
          if (docType === 'contrato') continue;
          const fileInfo = files[docType];
          if (!fileInfo?.required) continue;
          requested.add(docType);
          if (fileInfo.uploadedCount > 0) uploaded.add(docType);
        }
      }
      result[refId] = { uploaded: uploaded.size, requested: requested.size, total: osRelevantTotal, hasExpediente: true };
    }
    return result;
  }, [obligadosSolidarios, clientsById, linksById]);

  // Build a map of client afianzadora doc ID → active fianzas
  // Matching uses .includes() so catalog short names (e.g. "Berkley") match
  // full legal names from XML (e.g. "BERKLEY INTERNATIONAL COMPAÑIA DE GARANTIAS MEXICO")
  const fianzasByClientAfId = useMemo(() => {
    const now = Date.now();
    const map = {};

    // Collect all active fianzas first
    const activeFianzas = [];
    for (const contract of clientContracts) {
      for (const fianza of (contract.fianzas || [])) {
        const emisorNombre = getEmisorNombre(fianza);
        if (!emisorNombre) continue;
        // A cancelada / anulada / sustituida fianza no longer exposes the
        // afianzadora, so it must stop consuming the cliente's línea.
        if (getFianzaStatus(fianza).estado !== 'activa') continue;
        const fin = fianza.finVigenciaFianza || fianza.xmlFianza?.finVigenciaFianza;
        const finTs = typeof fin === 'number' ? fin : fin ? new Date(fin).getTime() : 0;
        if (finTs < now) continue;
        activeFianzas.push({
          ...fianza,
          _emisorNombre: emisorNombre.trim().toLowerCase(),
          contractId: contract.id,
          contratoId: contract.id,
        });
      }
    }

    // Match each client afianzadora by checking if emisor nombre includes the catalog name
    for (const ca of clientAfianzadoras) {
      const global = globalMap[ca.afianzadoraId];
      if (!global?.nombre) { map[ca.id] = []; continue; }
      const catalogName = global.nombre.trim().toLowerCase();
      map[ca.id] = activeFianzas.filter((f) => f._emisorNombre.includes(catalogName));
    }

    return map;
  }, [clientContracts, clientAfianzadoras, globalMap]);

  // Compute afianzado per cross-client afianzadora registration
  // Key: "refClientId/afianzadoraDocId" → { linea, afianzado, disponible }
  const crossClientLineaInfo = useMemo(() => {
    const info = {};
    const now = Date.now();
    for (const [refClientId, refAfizList] of Object.entries(crossClientAfianzadoras)) {
      const refContracts = allContracts.filter((c) => c.clientId === refClientId);
      const activeFianzas = [];
      for (const contract of refContracts) {
        for (const fianza of (contract.fianzas || [])) {
          const emisorNombre = getEmisorNombre(fianza);
          if (!emisorNombre) continue;
          if (getFianzaStatus(fianza).estado !== 'activa') continue;
          const fin = fianza.finVigenciaFianza || fianza.xmlFianza?.finVigenciaFianza;
          const finTs = typeof fin === 'number' ? fin : fin ? new Date(fin).getTime() : 0;
          if (finTs < now) continue;
          activeFianzas.push({
            _emisorNombre: emisorNombre.trim().toLowerCase(),
            montoAfianzado: fianza.montoAfianzado || fianza.xmlFianza?.montoAfianzado || 0,
          });
        }
      }
      for (const rca of refAfizList) {
        const global = globalMap[rca.afianzadoraId];
        if (!global?.nombre) continue;
        const catalogName = global.nombre.trim().toLowerCase();
        const matched = activeFianzas.filter((f) => f._emisorNombre.includes(catalogName));
        const afianzado = matched.reduce((sum, f) => sum + f.montoAfianzado, 0);
        const linea = rca.lineaAfianzamiento || 0;
        info[`${refClientId}/${rca.id}`] = { linea, afianzado, disponible: linea ? linea - afianzado : null };
      }
    }
    return info;
  }, [crossClientAfianzadoras, allContracts, globalMap]);

  // --- Handlers ---

  const handleAdd = async () => {
    if (!selectedAfianzadoraId) return;
    setAdding(true);
    try {
      const colRef = collection(db, 'clientes', clientId, 'afianzadoras');
      await addDoc(colRef, {
        afianzadoraId: selectedAfianzadoraId,
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        org: userData.org,
        lineaAfianzamiento: null,
        obligadosSolidarios: [],
        bienesInmuebles: [],
        files: {
          contratoMultiple: { status: 'pendiente', fileName: '', uploadedAt: null, storagePath: '', history: [] },
          revisionBuro: { status: 'pendiente', fileName: '', uploadedAt: null, storagePath: '' },
          entrevista: { status: 'pendiente', fileName: '', uploadedAt: null, storagePath: '', history: [] },
        },
      });
      setShowAddDialog(false);
      setSelectedAfianzadoraId(null);
    } catch (err) {
      console.error('Error adding afianzadora:', err);
    } finally {
      setAdding(false);
    }
  };

  const openUploadDialog = (docId, fileType, afianzadoraName, isReplace = false) => {
    setUploadTarget({ docId, fileType, afianzadoraName, isReplace });
    setUploadFile(null);
    setShowUploadDialog(true);
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadTarget) return;
    setUploading(true);
    try {
      const { docId, fileType, isReplace } = uploadTarget;
      const file = uploadFile;

      // If replacing, push current version to history before the new upload
      if (isReplace) {
        const ca = clientAfianzadoras.find((c) => c.id === docId);
        const currentFileData = ca?.files?.[fileType];
        if (currentFileData?.fileName && currentFileData?.storagePath) {
          await updateDoc(doc(db, 'clientes', clientId, 'afianzadoras', docId), {
            [`files.${fileType}.history`]: arrayUnion({
              fileName: currentFileData.fileName,
              uploadedAt: currentFileData.uploadedAt,
              storagePath: currentFileData.storagePath,
            }),
          });
        }
      }

      // Backend handles Storage upload + processing
      await uploadDocumentFile(clientId, 'afianzadoras', docId, fileType, file);

      setShowUploadDialog(false);
      setUploadTarget(null);
      setUploadFile(null);
    } catch (err) {
      console.error('Error uploading file:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'clientes', clientId, 'afianzadoras', deleteTarget));
      setShowDeleteDialog(false);
      setDeleteTarget(null);
    } catch (err) {
      console.error('Error deleting afianzadora:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveLinea = async (docId) => {
    const numVal = parseFormattedNumber(lineaValue);
    if (!numVal) return;
    setSavingLinea(true);
    try {
      await updateDoc(doc(db, 'clientes', clientId, 'afianzadoras', docId), {
        lineaAfianzamiento: numVal,
      });
      setEditingLinea(null);
    } catch (err) {
      console.error('Error saving linea:', err);
    } finally {
      setSavingLinea(false);
    }
  };

  // Vincular/Desvincular OS
  const handleVincularOS = async (afianzadoraDocId) => {
    if (!selectedOSToLink) return;
    try {
      const os = osMap[selectedOSToLink];
      const isClientRef = os?.clienteRef || os?.tipo === 'cliente_existente';
      // Check if client-ref OS is already registered with this afianzadora
      const osAlreadyRegistered = isClientRef && os?.clienteRef && (() => {
        const refAfiz = crossClientAfianzadoras[os.clienteRef] || [];
        const ca = clientAfianzadoras.find((c) => c.id === afianzadoraDocId);
        return refAfiz.some((rca) => rca.afianzadoraId === ca?.afianzadoraId);
      })();

      const updates = {
        obligadosSolidarios: arrayUnion(selectedOSToLink),
      };

      // Initialize per-afianzadora OS docs (skip if client-ref already registered)
      if (!osAlreadyRegistered) {
        const osFileSlots = {};
        OS_AFIANZADORA_DOCS.forEach((d) => {
          osFileSlots[d.id] = { status: 'pendiente', fileName: '', uploadedAt: null, storagePath: '' };
        });
        updates[`osFiles.${selectedOSToLink}`] = osFileSlots;
      }

      await updateDoc(doc(db, 'clientes', clientId, 'afianzadoras', afianzadoraDocId), updates);
      setShowVincularOS(null);
      setSelectedOSToLink(null);
    } catch (err) {
      console.error('Error vinculando OS:', err);
    }
  };

  const handleDesvincularOS = async (afianzadoraDocId, osId) => {
    try {
      await updateDoc(doc(db, 'clientes', clientId, 'afianzadoras', afianzadoraDocId), {
        obligadosSolidarios: arrayRemove(osId),
        [`osFiles.${osId}`]: deleteField(),
      });
    } catch (err) {
      console.error('Error desvinculando OS:', err);
    }
  };

  // Upload per-afianzadora OS file (entrevista / consultaBuro)
  const handleOsFileUpload = async () => {
    if (!osFileUploadFile || !osFileUpload) return;
    const { osId, fileType } = osFileUpload;
    setOsFileUploading(true);
    try {
      // Backend handles Storage upload + processing
      await uploadDocumentFile(clientId, 'obligados_solidarios', osId, fileType, osFileUploadFile);

      setOsFileUpload(null);
      setOsFileUploadFile(null);
    } catch (err) {
      console.error('Error uploading OS afianzadora file:', err);
    } finally {
      setOsFileUploading(false);
    }
  };

  // Vincular/Desvincular BI (composite refs: "clientId/bienId")
  const handleVincularBI = async (afianzadoraDocId) => {
    if (!selectedBIToLink) return;
    try {
      await updateDoc(doc(db, 'clientes', clientId, 'afianzadoras', afianzadoraDocId), {
        bienesInmuebles: arrayUnion(selectedBIToLink),
      });
      // Add crossClientRefs back-reference if cross-client
      const { clientId: biClientId, bienId } = parseBienRef(selectedBIToLink);
      if (biClientId && biClientId !== clientId) {
        await updateDoc(doc(db, 'clientes', biClientId, 'bienes_inmuebles', bienId), {
          crossClientRefs: arrayUnion(clientId),
        });
      }
      setShowVincularBI(null);
      setSelectedBIToLink(null);
    } catch (err) {
      console.error('Error vinculando BI:', err);
    }
  };

  const handleDesvincularBI = async (afianzadoraDocId, biRef) => {
    try {
      await updateDoc(doc(db, 'clientes', clientId, 'afianzadoras', afianzadoraDocId), {
        bienesInmuebles: arrayRemove(biRef),
      });
      // Remove crossClientRefs back-reference if cross-client
      const { clientId: biClientId, bienId } = parseBienRef(biRef);
      if (biClientId && biClientId !== clientId) {
        // Check if this client still has other afianzadoras linking to this bien
        const stillLinked = clientAfianzadoras.some((ca) =>
          ca.id !== afianzadoraDocId && (ca.bienesInmuebles || []).includes(biRef)
        );
        if (!stillLinked) {
          await updateDoc(doc(db, 'clientes', biClientId, 'bienes_inmuebles', bienId), {
            crossClientRefs: arrayRemove(clientId),
          });
        }
      }
    } catch (err) {
      console.error('Error desvinculando BI:', err);
    }
  };

  // Delete OS/BI at client level
  const handleDeleteOS = async (osId) => {
    setDeletingEntity(true);
    try {
      await deleteDoc(doc(db, 'clientes', clientId, 'obligados_solidarios', osId));
      // Also remove from any afianzadora references
      for (const ca of clientAfianzadoras) {
        if (ca.obligadosSolidarios?.includes(osId)) {
          await updateDoc(doc(db, 'clientes', clientId, 'afianzadoras', ca.id), {
            obligadosSolidarios: arrayRemove(osId),
          });
        }
      }
      setShowDeleteOSDialog(null);
    } catch (err) {
      console.error('Error deleting OS:', err);
    } finally {
      setDeletingEntity(false);
    }
  };

  const handleDeleteBI = async (biId) => {
    setDeletingEntity(true);
    try {
      await deleteDoc(doc(db, 'clientes', clientId, 'bienes_inmuebles', biId));
      // Remove from any afianzadora references (both old format and composite)
      const biRef = `${clientId}/${biId}`;
      for (const ca of clientAfianzadoras) {
        if (ca.bienesInmuebles?.includes(biId)) {
          await updateDoc(doc(db, 'clientes', clientId, 'afianzadoras', ca.id), {
            bienesInmuebles: arrayRemove(biId),
          });
        }
        if (ca.bienesInmuebles?.includes(biRef)) {
          await updateDoc(doc(db, 'clientes', clientId, 'afianzadoras', ca.id), {
            bienesInmuebles: arrayRemove(biRef),
          });
        }
      }
      setShowDeleteBIDialog(null);
    } catch (err) {
      console.error('Error deleting BI:', err);
    } finally {
      setDeletingEntity(false);
    }
  };

  // --- Helpers ---

  const getVersionNumber = (fileData) => {
    const histLen = fileData?.history?.length || 0;
    if (fileData?.status !== 'pendiente') return histLen + 1;
    return 0;
  };

  // --- Render ---

  const loading = loadingGlobal || loadingClient;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-azul-surelia" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Heading level={2} className="text-xl font-bold text-azul-surelia">
            Afianzadoras
          </Heading>
          <Text className="text-zinc-500 text-sm mt-1">
            Relaciones con afianzadoras de {clientData?.nombreEmpresa || 'este cliente'}.
          </Text>
        </div>
        <Button outline color="surelia" onClick={() => { setSelectedAfianzadoraId(null); setShowAddDialog(true); }}>
          Agregar afianzadora
        </Button>
      </div>

      {/* Empty state */}
      {clientAfianzadoras.length === 0 && (
        <div className="text-center py-16 border-2 border-dashed border-zinc-200 rounded-xl">
          <ShieldCheckIcon className="w-12 h-12 mx-auto text-zinc-300 mb-3" />
          <Text className="text-zinc-500 font-medium">
            No hay afianzadoras asociadas a este cliente.
          </Text>
          <Text className="text-zinc-400 text-sm mt-1">
            Agrega una afianzadora para comenzar a gestionar documentos.
          </Text>
        </div>
      )}

      {/* Afianzadora Cards (full width, stacked) */}
      <div className="space-y-4">
        {clientAfianzadoras.map((ca) => {
          const global = globalMap[ca.afianzadoraId];
          const nombre = global?.nombre || 'Afianzadora desconocida';
          const logo = global?.logo || null;
          const linkedOS = (ca.obligadosSolidarios || []).map((id) => osMap[id]).filter(Boolean);
          const linkedBI = (ca.bienesInmuebles || []).map((ref) => biMap[ref]).filter(Boolean);

          // Active fianzas matched to this afianzadora
          const activeFianzas = fianzasByClientAfId[ca.id] || [];
          const totalAfianzado = activeFianzas.reduce((sum, f) => {
            const monto = f.montoAfianzado || f.xmlFianza?.montoAfianzado || 0;
            return sum + monto;
          }, 0);
          const linea = ca.lineaAfianzamiento || 0;
          const lineaDisponible = linea > 0 ? linea - totalAfianzado : null;

          return (
            <div
              key={ca.id}
              className="border border-zinc-200 rounded-xl p-5 bg-white shadow-sm"
            >
              {/* Card header */}
              <div className="flex items-center gap-3 mb-4">
                {logo ? (
                  <img src={logo} alt={nombre} className="w-10 h-10 rounded-lg object-contain bg-zinc-50 border border-zinc-100 p-1" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-azul-surelia/10 flex items-center justify-center text-azul-surelia font-bold text-sm">
                    {nombre.charAt(0)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-zinc-900 truncate">{nombre}</h3>
                </div>
                {isOwner && (
                  <button
                    onClick={() => { setDeleteTarget(ca.id); setShowDeleteDialog(true); }}
                    className="p-1.5 rounded-lg text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    title="Eliminar relacion"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Linea de Afianzamiento */}
              <div className="mb-4 px-3 py-2 bg-zinc-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <CurrencyDollarIcon className="w-4 h-4 text-zinc-400" />
                  <span className="text-sm font-medium text-zinc-600">Linea de Afianzamiento</span>
                </div>
                {editingLinea === ca.id ? (
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex items-center flex-1 px-2 py-1 text-sm border border-zinc-300 rounded-md focus-within:ring-1 focus-within:ring-azul-surelia bg-white">
                      <span className="text-zinc-400 mr-1">$</span>
                      <input
                        type="text"
                        value={lineaValue}
                        onChange={(e) => setLineaValue(formatInputWithCommas(e.target.value))}
                        className="flex-1 outline-none bg-transparent"
                        placeholder="5,000,000"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveLinea(ca.id);
                          if (e.key === 'Escape') setEditingLinea(null);
                        }}
                      />
                    </div>
                    <Button color="surelia" onClick={() => handleSaveLinea(ca.id)} disabled={savingLinea}>
                      {savingLinea ? '...' : 'Guardar'}
                    </Button>
                    <Button plain onClick={() => setEditingLinea(null)}>Cancelar</Button>
                  </div>
                ) : (
                  <div>
                    <button
                      onClick={() => { setEditingLinea(ca.id); setLineaValue(ca.lineaAfianzamiento ? formatInputWithCommas(String(ca.lineaAfianzamiento)) : ''); }}
                      className="mt-1 text-sm text-zinc-800 hover:text-azul-surelia transition-colors"
                    >
                      {ca.lineaAfianzamiento ? formatMXN(ca.lineaAfianzamiento) : <span className="text-zinc-400 italic">Sin definir — click para editar</span>}
                    </button>
                    {linea > 0 && activeFianzas.length > 0 && (
                      <div className="mt-1.5 flex items-center gap-4 text-xs">
                        <span className="text-zinc-500">Afianzado: <span className="font-semibold text-zinc-700">{formatMXN(totalAfianzado)}</span></span>
                        <span className={`font-semibold ${lineaDisponible < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                          Disponible: {formatMXN(lineaDisponible)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Documents section */}
              <div className="mb-4">
                <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2 px-1">Documentos</h4>
                <div className="space-y-2">
                  {FILE_TYPES.map((ft) => {
                    const fileData = ca.files?.[ft.id] || { status: 'pendiente' };
                    const statusInfo = STATUS_BADGE[fileData.status] || STATUS_BADGE.pendiente;
                    const vNum = getVersionNumber(fileData);
                    const hasFile = fileData.status !== 'pendiente';
                    const hasHistory = ft.versionable && fileData.history?.length > 0;

                    return (
                      <div
                        key={ft.id}
                        className="flex items-center justify-between gap-2 py-2 px-3 rounded-lg bg-zinc-50"
                      >
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-zinc-700">{ft.label}</span>
                          {fileData.fileName && (
                            <span className="block text-xs text-zinc-400 truncate" title={fileData.fileName}>
                              {fileData.fileName}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Badge color={statusInfo.color}>
                            {statusInfo.label}{vNum > 0 && ft.versionable ? ` v${vNum}` : ''}
                          </Badge>
                          {!hasFile && (
                            <button
                              onClick={() => openUploadDialog(ca.id, ft.id, nombre, false)}
                              className="p-1.5 rounded-lg text-azul-surelia hover:bg-azul-surelia/10 transition-colors"
                              title={`Subir ${ft.label}`}
                            >
                              <ArrowUpTrayIcon className="w-4 h-4" />
                            </button>
                          )}
                          {hasFile && ft.versionable && (
                            <button
                              onClick={() => openUploadDialog(ca.id, ft.id, nombre, true)}
                              className="p-1.5 rounded-lg text-azul-surelia hover:bg-azul-surelia/10 transition-colors"
                              title="Reemplazar"
                            >
                              <ArrowUpTrayIcon className="w-4 h-4" />
                            </button>
                          )}
                          {fileData.status === 'procesado' && fileData.results && (
                            <button
                              onClick={async () => {
                                let pdfUrl = null;
                                if (fileData.storagePath) {
                                  try {
                                    pdfUrl = await getDownloadURL(ref(storage, fileData.storagePath));
                                  } catch (e) {
                                    console.warn('Could not get PDF URL:', e);
                                  }
                                }
                                // Collect only line IDs referenced by sources
                                const referencedLineIds = new Set();
                                if (fileData.results) {
                                  for (const field of Object.values(fileData.results)) {
                                    if (Array.isArray(field?.sources)) {
                                      field.sources.forEach(s => referencedLineIds.add(s));
                                    }
                                    // errors nested fields
                                    if (field?.hasError?.sources) field.hasError.sources.forEach(s => referencedLineIds.add(s));
                                    if (field?.errorMessage?.sources) field.errorMessage.sources.forEach(s => referencedLineIds.add(s));
                                    // Nested array items (obligadosSolidarios, garantiasInmobiliarias)
                                    if (Array.isArray(field)) {
                                      field.forEach(item => {
                                        if (Array.isArray(item?.sources)) {
                                          item.sources.forEach(s => referencedLineIds.add(s));
                                        }
                                      });
                                    }
                                  }
                                }
                                const allLines = fileData.lines || {};
                                const filteredLines = {};
                                for (const id of referencedLineIds) {
                                  if (allLines[id]) filteredLines[id] = allLines[id];
                                }

                                setProcessedViewData({
                                  processedFileData: {
                                    results: fileData.results,
                                    lines: filteredLines,
                                  },
                                  fileType: ft.id,
                                  fileName: fileData.fileName,
                                  fileLabel: ft.label,
                                  pdfUrl,
                                });
                                setIsProcessedViewOpen(true);
                              }}
                              className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors"
                              title="Ver datos extraídos"
                            >
                              <EyeIcon className="w-4 h-4" />
                            </button>
                          )}
                          {fileData.status === 'procesado' && fileData.storagePath && (
                            <button
                              onClick={async () => {
                                const reprocessKey = `${ca.id}_${ft.id}`;
                                setReprocessingFiles(prev => new Set([...prev, reprocessKey]));
                                try {
                                  await reprocessDocumentFile(clientId, 'afianzadoras', ca.id, ft.id);
                                } catch (e) {
                                  console.error('Error reprocessing:', e);
                                } finally {
                                  setReprocessingFiles(prev => {
                                    const next = new Set(prev);
                                    next.delete(reprocessKey);
                                    return next;
                                  });
                                }
                              }}
                              disabled={reprocessingFiles.has(`${ca.id}_${ft.id}`)}
                              className="p-1.5 rounded-lg text-zinc-400 hover:text-amber-600 hover:bg-amber-50 transition-colors disabled:opacity-50"
                              title="Reprocesar"
                            >
                              <ArrowPathIcon className={`w-4 h-4 ${reprocessingFiles.has(`${ca.id}_${ft.id}`) ? 'animate-spin' : ''}`} />
                            </button>
                          )}
                          {ft.versionable && (hasHistory || hasFile) && (
                            <button
                              onClick={() => setHistoryTarget({ history: fileData.history || [], currentFile: fileData, label: ft.label })}
                              className="p-1.5 rounded-lg text-zinc-400 hover:text-azul-surelia hover:bg-azul-surelia/10 transition-colors"
                              title="Historial"
                            >
                              <ClockIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Active Fianzas from contracts */}
              {activeFianzas.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2 px-1">
                    Fianzas Activas ({activeFianzas.length})
                  </h4>
                  <div className="space-y-1">
                    {activeFianzas.map((f, idx) => {
                      const monto = f.montoAfianzado || f.xmlFianza?.montoAfianzado || 0;
                      const numero = f.numeroFianza || f.xmlFianza?.numeroFianza || '';
                      const tipo = f.tipoFianza || f.xmlFianza?.tipo || '';
                      const fin = f.finVigenciaFianza || f.xmlFianza?.finVigenciaFianza;
                      const finDate = fin ? new Date(typeof fin === 'number' ? fin : fin).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
                      return (
                        <button
                          key={f.id || idx}
                          onClick={() => { setSelectedFianza(f); setIsFianzaViewOpen(true); }}
                          className="w-full flex items-center justify-between gap-2 py-1.5 px-3 rounded-lg bg-zinc-50 hover:bg-azul-surelia/5 transition-colors text-left"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <DocumentTextIcon className="w-4 h-4 text-azul-surelia flex-shrink-0" />
                            <div className="min-w-0">
                              <span className="text-sm font-medium text-zinc-700 truncate block">
                                {numero ? `#${numero}` : 'Fianza'}{tipo ? ` — ${tipo}` : ''}
                              </span>
                              {finDate && <span className="text-xs text-zinc-400">Vence: {finDate}</span>}
                            </div>
                          </div>
                          <span className="text-sm font-semibold text-zinc-700 whitespace-nowrap">{formatMXN(monto)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Obligados Solidarios linked to this afianzadora */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2 px-1">
                  <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Obligados Solidarios</h4>
                  <button
                    onClick={() => { setShowVincularOS(ca.id); setSelectedOSToLink(null); }}
                    className="flex items-center gap-1 text-xs text-azul-surelia hover:text-azul-surelia/80 font-medium"
                  >
                    <LinkIcon className="w-3 h-3" /> Vincular
                  </button>
                </div>
                {linkedOS.length === 0 ? (
                  <p className="text-xs text-zinc-400 px-3 py-2">Sin obligados solidarios vinculados.</p>
                ) : (
                  <div className="space-y-1">
                    {linkedOS.map((os) => {
                      const isClientRef = os.clienteRef || os.tipo === 'cliente_existente';
                      const docs = isClientRef ? [] : getOsDocs(os.tipo);
                      const { done, total } = isClientRef ? { done: 0, total: 0 } : countUploaded(os.files, docs);
                      const osRefAfianzadoras = isClientRef && os.clienteRef ? (crossClientAfianzadoras[os.clienteRef] || []) : [];
                      // Check if client-ref OS is already registered with this afianzadora
                      const osAlreadyRegistered = isClientRef && os.clienteRef && osRefAfianzadoras.some(
                        (rca) => rca.afianzadoraId === ca.afianzadoraId
                      );
                      // Find the OS client's registration with this same afianzadora to show línea disponible
                      //const osMatchingReg = isClientRef ? osRefAfianzadoras.find((rca) => rca.afianzadoraId === ca.afianzadoraId) : null;
                      //const osLineaKey = osMatchingReg && os.clienteRef ? `${os.clienteRef}/${osMatchingReg.id}` : null;
                      //const osLineaData = osLineaKey ? crossClientLineaInfo[osLineaKey] : null; // eslint-disable-line no-unused-vars
                      const osFilesForThis = ca.osFiles?.[os.id] || {};
                      const showOsAfizDocs = !osAlreadyRegistered;
                      return (
                        <div
                          key={os.id}
                          className="py-1.5 px-3 rounded-lg bg-zinc-50 cursor-pointer hover:bg-zinc-100 transition-colors"
                          onClick={() => { setOsDialogData(os); setShowOSDialog(true); }}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              {isClientRef ? (
                                <ArrowTopRightOnSquareIcon className="w-4 h-4 text-purple-500 flex-shrink-0" />
                              ) : os.tipo === 'persona_moral' ? (
                                <BuildingOfficeIcon className="w-4 h-4 text-amber-500 flex-shrink-0" />
                              ) : (
                                <UserIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
                              )}
                              <span className="text-sm font-medium truncate">{os.nombre}</span>
                              {isClientRef && <span className="text-xs text-zinc-400">(Cliente)</span>}
                            </div>
                            <div className="flex items-center gap-2">
                              {isClientRef ? (
                                <Badge color="purple">Ver expediente</Badge>
                              ) : (
                                <Badge color={done === total ? 'green' : 'zinc'}>Docs {done}/{total}</Badge>
                              )}
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDesvincularOS(ca.id, os.id); }}
                                className="p-1 rounded text-zinc-400 hover:text-red-500 transition-colors"
                                title="Desvincular"
                              >
                                <XMarkIcon className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                          {osRefAfianzadoras.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-1.5 ml-6">
                              {osRefAfianzadoras.map((rca) => {
                                const g = globalMap[rca.afianzadoraId];
                                const n = g?.nombre || '?';
                                const pillKey = os.clienteRef ? `${os.clienteRef}/${rca.id}` : null;
                                const pillData = pillKey ? crossClientLineaInfo[pillKey] : null;
                                const disp = pillData?.disponible;
                                const hasDisp = disp !== null && disp !== undefined;
                                return (
                                  <span key={rca.id} className="inline-flex items-center rounded-full border border-purple-200 text-[11px] font-medium overflow-hidden">
                                    <span className="px-2 py-0.5 bg-purple-50 text-purple-700">{n}</span>
                                    {hasDisp && (
                                      <span className={`px-1.5 py-0.5 border-l ${disp >= 0 ? 'bg-zinc-50 border-zinc-200 text-zinc-600' : 'bg-red-50 border-red-200 text-red-600'}`}>
                                        {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(disp)}
                                      </span>
                                    )}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                          {/* Per-afianzadora OS docs (entrevista, consulta buro) */}
                          {showOsAfizDocs && (
                            <div className="mt-2 ml-6 space-y-1" onClick={(e) => e.stopPropagation()}>
                              {OS_AFIANZADORA_DOCS.map((d) => {
                                const fileData = osFilesForThis[d.id] || { status: 'pendiente' };
                                const statusInfo = STATUS_BADGE[fileData.status] || STATUS_BADGE.pendiente;
                                const isActiveUpload = osFileUpload?.afianzadoraDocId === ca.id && osFileUpload?.osId === os.id && osFileUpload?.fileType === d.id;
                                return (
                                  <div key={d.id}>
                                    <div className="flex items-center justify-between gap-2 py-1 px-2 rounded bg-white border border-zinc-100">
                                      <span className="text-xs text-zinc-600">{d.label}</span>
                                      <div className="flex items-center gap-1.5">
                                        <Badge color={statusInfo.color}>{statusInfo.label}</Badge>
                                        {fileData.status === 'pendiente' && (
                                          <button
                                            onClick={() => {
                                              setOsFileUpload(isActiveUpload ? null : { afianzadoraDocId: ca.id, osId: os.id, fileType: d.id });
                                              setOsFileUploadFile(null);
                                            }}
                                            className="p-1 rounded text-azul-surelia hover:bg-azul-surelia/10 transition-colors"
                                            title={`Subir ${d.label}`}
                                          >
                                            <ArrowUpTrayIcon className="w-3.5 h-3.5" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    {isActiveUpload && (
                                      <div className="mt-1 ml-2">
                                        <FileReceiver
                                          title=""
                                          type="pdf"
                                          maxFiles={1}
                                          onFilesChange={(files) => setOsFileUploadFile(files[0] || null)}
                                          showFileName
                                          currentFile={osFileUploadFile}
                                          onRemoveFile={() => setOsFileUploadFile(null)}
                                        />
                                        {osFileUploadFile && (
                                          <div className="mt-1 flex justify-end">
                                            <Button color="surelia" onClick={handleOsFileUpload} disabled={osFileUploading}>
                                              {osFileUploading ? 'Subiendo...' : 'Subir'}
                                            </Button>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {osAlreadyRegistered && (
                            <p className="text-[10px] text-green-600 mt-1 ml-6">Ya registrado con esta afianzadora — no requiere entrevista ni consulta buro.</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Bienes Inmuebles linked to this afianzadora */}
              <div>
                <div className="flex items-center justify-between mb-2 px-1">
                  <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Bienes Inmuebles en Garantia</h4>
                  <button
                    onClick={() => { setShowVincularBI(ca.id); setSelectedBIToLink(null); }}
                    className="flex items-center gap-1 text-xs text-azul-surelia hover:text-azul-surelia/80 font-medium"
                  >
                    <LinkIcon className="w-3 h-3" /> Vincular
                  </button>
                </div>
                {linkedBI.length === 0 ? (
                  <p className="text-xs text-zinc-400 px-3 py-2">Sin bienes inmuebles vinculados.</p>
                ) : (
                  <div className="space-y-1">
                    {linkedBI.map((bi) => {
                      const { done, total } = countUploaded(bi.files, BIEN_INMUEBLE_DOCS);
                      const isCrossClient = bi._ownerClientId !== clientId;
                      const ownerClientName = isCrossClient ? (clientsById[bi._ownerClientId]?.nombreEmpresa || clientsById[bi._ownerClientId]?.nombre || '') : '';
                      const biRef = `${bi._ownerClientId}/${bi.id}`;
                      return (
                        <div
                          key={biRef}
                          className="flex items-center justify-between gap-2 py-1.5 px-3 rounded-lg bg-zinc-50 cursor-pointer hover:bg-zinc-100 transition-colors"
                          onClick={() => { setBiDialogData(bi); setShowBIDialog(true); }}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <HomeModernIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                            <div className="min-w-0">
                              <span className="text-sm font-medium truncate block">{bi.descripcion}</span>
                              <span className="text-xs text-zinc-400">de {bi.ownerNombre || 'Cliente'}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {isCrossClient && (
                              <Badge color="purple">{ownerClientName || 'Otro cliente'}</Badge>
                            )}
                            <Badge color={done === total ? 'green' : 'zinc'}>Docs {done}/{total}</Badge>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDesvincularBI(ca.id, biRef); }}
                              className="p-1 rounded text-zinc-400 hover:text-red-500 transition-colors"
                              title="Desvincular"
                            >
                              <XMarkIcon className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ═══════════════ Client-level OS & BI sections ═══════════════ */}
      <div className="mt-10 border-t border-zinc-200 pt-8">
        {/* Obligados Solidarios section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <Heading level={3} className="text-lg font-bold text-zinc-800">
                Obligados Solidarios
              </Heading>
              <Text className="text-zinc-400 text-sm">Obligados solidarios de este cliente.</Text>
            </div>
            <Button outline color="surelia" onClick={() => { setOsDialogData(null); setShowOSDialog(true); }}>
              <PlusIcon className="w-4 h-4 mr-1" /> Agregar OS
            </Button>
          </div>

          {obligadosSolidarios.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-zinc-200 rounded-xl">
              <UserIcon className="w-10 h-10 mx-auto text-zinc-300 mb-2" />
              <Text className="text-zinc-400 text-sm">No hay obligados solidarios registrados.</Text>
            </div>
          ) : (
            <div className="border border-zinc-200 rounded-xl overflow-hidden">
              {obligadosSolidarios.map((os, idx) => {
                const isClientRef = os.clienteRef || os.tipo === 'cliente_existente';
                const docs = isClientRef ? [] : getOsDocs(os.tipo);
                const { done, total } = isClientRef ? { done: 0, total: 0 } : countUploaded(os.files, docs);
                const tipoLabel = isClientRef ? 'Cliente existente' : os.tipo === 'persona_moral' ? 'Persona Moral' : 'Persona Fisica';
                const osRefAfiz = isClientRef && os.clienteRef ? (crossClientAfianzadoras[os.clienteRef] || []) : [];
                const expStatus = isClientRef && os.clienteRef ? osRefExpedienteStatus[os.clienteRef] : null;
                return (
                  <div
                    key={os.id}
                    className={`px-4 py-3 ${idx > 0 ? 'border-t border-zinc-100' : ''} hover:bg-zinc-50 transition-colors cursor-pointer`}
                    onClick={() => { setOsDialogData(os); setShowOSDialog(true); }}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          isClientRef ? 'bg-purple-100 text-purple-700' : os.tipo === 'persona_moral' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {isClientRef ? <ArrowTopRightOnSquareIcon className="w-4 h-4" /> : os.tipo === 'persona_moral' ? <BuildingOfficeIcon className="w-4 h-4" /> : <UserIcon className="w-4 h-4" />}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-zinc-800 truncate">{os.nombre}</p>
                          <p className="text-xs text-zinc-400">{tipoLabel}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isClientRef && expStatus ? (
                          !expStatus.hasExpediente ? (
                            <Badge color="zinc">Sin expediente</Badge>
                          ) : expStatus.uploaded === expStatus.requested && expStatus.requested > 0 ? (
                            <Badge color="green">Expediente completo</Badge>
                          ) : expStatus.requested > 0 ? (
                            <Badge color="amber">Docs {expStatus.uploaded}/{expStatus.requested}</Badge>
                          ) : (
                            <Badge color="zinc">Sin solicitar</Badge>
                          )
                        ) : !isClientRef ? (
                          <Badge color={done === total ? 'green' : 'zinc'}>Docs {done}/{total}</Badge>
                        ) : null}
                        {isOwner && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setShowDeleteOSDialog(os.id); }}
                            className="p-1.5 rounded-lg text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Eliminar OS"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    {/* Afianzadora registrations for client-ref OS */}
                    {osRefAfiz.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2 ml-11">
                        {osRefAfiz.map((rca) => {
                          const g = globalMap[rca.afianzadoraId];
                          const n = g?.nombre || '?';
                          return (
                            <span key={rca.id} className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-50 border border-purple-200 text-[11px] font-medium text-purple-700">
                              {n}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Bienes Inmuebles section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <Heading level={3} className="text-lg font-bold text-zinc-800">
                Bienes Inmuebles
              </Heading>
              <Text className="text-zinc-400 text-sm">Bienes inmuebles en garantia de este cliente.</Text>
            </div>
            <Button outline color="surelia" onClick={() => { setBiDialogData(null); setShowBIDialog(true); }}>
              <PlusIcon className="w-4 h-4 mr-1" /> Agregar BI
            </Button>
          </div>

          {bienesInmuebles.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-zinc-200 rounded-xl">
              <HomeModernIcon className="w-10 h-10 mx-auto text-zinc-300 mb-2" />
              <Text className="text-zinc-400 text-sm">No hay bienes inmuebles registrados.</Text>
            </div>
          ) : (
            <div className="border border-zinc-200 rounded-xl overflow-hidden">
              {bienesInmuebles.map((bi, idx) => {
                const { done, total } = countUploaded(bi.files, BIEN_INMUEBLE_DOCS);
                return (
                  <div
                    key={bi.id}
                    className={`flex items-center justify-between gap-3 px-4 py-3 ${idx > 0 ? 'border-t border-zinc-100' : ''} hover:bg-zinc-50 transition-colors cursor-pointer`}
                    onClick={() => { setBiDialogData({ ...bi, _ownerClientId: clientId }); setShowBIDialog(true); }}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                        <HomeModernIcon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-zinc-800 truncate">{bi.descripcion}</p>
                        <p className="text-xs text-zinc-400">Propietario: {bi.ownerNombre || 'Cliente'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge color={done === total ? 'green' : 'zinc'}>Docs {done}/{total}</Badge>
                      {isOwner && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowDeleteBIDialog(bi.id); }}
                          className="p-1.5 rounded-lg text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Eliminar bien inmueble"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Cross-client bienes from OS-referenced clients */}
          {osRefClientIds.length > 0 && Object.values(crossClientBienes).some((arr) => arr.length > 0) && (
            <div className="mt-6">
              <Heading level={4} className="text-base font-bold text-zinc-700 mb-3">
                Bienes de Obligados Solidarios
              </Heading>
              <Text className="text-zinc-400 text-xs mb-3">
                Bienes inmuebles que pertenecen a clientes vinculados como obligados solidarios. Solo lectura — se pueden vincular a afianzadoras desde las tarjetas de arriba.
              </Text>
              <div className="border border-zinc-200 rounded-xl overflow-hidden">
                {osRefClientIds.map((refClientId) => {
                  const bienes = crossClientBienes[refClientId] || [];
                  if (bienes.length === 0) return null;
                  const refClientName = clientsById[refClientId]?.nombreEmpresa || clientsById[refClientId]?.nombre || refClientId;
                  return bienes.map((bi, idx) => {
                    const { done, total } = countUploaded(bi.files, BIEN_INMUEBLE_DOCS);
                    return (
                      <div
                        key={`${refClientId}/${bi.id}`}
                        className={`flex items-center justify-between gap-3 px-4 py-3 ${idx > 0 ? 'border-t border-zinc-100' : ''} hover:bg-zinc-50 transition-colors cursor-pointer`}
                        onClick={() => { setBiDialogData({ ...bi, _ownerClientId: refClientId }); setShowBIDialog(true); }}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center">
                            <HomeModernIcon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-zinc-800 truncate">{bi.descripcion}</p>
                            <p className="text-xs text-zinc-400">de {bi.ownerNombre || 'Cliente'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge color="purple">{refClientName}</Badge>
                          <Badge color={done === total ? 'green' : 'zinc'}>Docs {done}/{total}</Badge>
                        </div>
                      </div>
                    );
                  });
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ═══════════════ DIALOGS ═══════════════ */}

      {/* Add Afianzadora Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} size="sm">
        <DialogTitle>Agregar afianzadora</DialogTitle>
        <DialogBody>
          {addOptions.length === 0 ? (
            <Text className="text-zinc-500">Todas las afianzadoras disponibles ya han sido agregadas.</Text>
          ) : (
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">Selecciona una afianzadora</label>
              <SearchableListbox
                options={addOptions}
                value={selectedAfianzadoraId}
                onChange={setSelectedAfianzadoraId}
                placeholder="Selecciona una afianzadora..."
                searchPlaceholder="Buscar afianzadora..."
                aria-label="Buscar afianzadora"
                popupClassName="w-full"
              />
            </div>
          )}
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setShowAddDialog(false)}>Cancelar</Button>
          {addOptions.length > 0 && (
            <Button color="surelia" onClick={handleAdd} disabled={!selectedAfianzadoraId || adding}>
              {adding ? 'Agregando...' : 'Agregar'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Upload Dialog (new upload or replace) */}
      <Dialog open={showUploadDialog} onClose={() => setShowUploadDialog(false)} size="sm">
        <DialogTitle>{uploadTarget?.isReplace ? 'Reemplazar documento' : 'Subir documento'}</DialogTitle>
        <DialogBody>
          {uploadTarget && (
            <div>
              <Text className="text-zinc-500 text-sm mb-4">
                {FILE_TYPES.find((ft) => ft.id === uploadTarget.fileType)?.label} para{' '}
                <span className="font-semibold">{uploadTarget.afianzadoraName}</span>
                {uploadTarget.isReplace && (
                  <span className="block text-xs text-amber-600 mt-1">
                    La version actual se movera al historial.
                  </span>
                )}
              </Text>
              <FileReceiver
                title="Archivo PDF"
                type="pdf"
                maxFiles={1}
                onFilesChange={(files) => setUploadFile(files[0] || null)}
                showFileName
                currentFile={uploadFile}
                onRemoveFile={() => setUploadFile(null)}
              />
            </div>
          )}
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => { setShowUploadDialog(false); setUploadFile(null); }}>Cancelar</Button>
          <Button color="surelia" onClick={handleUpload} disabled={!uploadFile || uploading}>
            {uploading ? 'Subiendo...' : uploadTarget?.isReplace ? 'Reemplazar' : 'Subir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Afianzadora Confirmation */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} size="xs">
        <DialogTitle>Eliminar afianzadora</DialogTitle>
        <DialogBody>
          <Text className="text-zinc-600">
            ¿Estas seguro de que deseas eliminar esta relacion con la afianzadora? Esta accion no se puede deshacer.
          </Text>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setShowDeleteDialog(false)}>Cancelar</Button>
          <Button color="red" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* File History Popover */}
      <FileHistoryPopover
        open={!!historyTarget}
        onClose={() => setHistoryTarget(null)}
        history={historyTarget?.history || []}
        currentFile={historyTarget?.currentFile || null}
        label={historyTarget?.label || ''}
      />

      {/* Vincular OS Dialog */}
      <Dialog open={!!showVincularOS} onClose={() => setShowVincularOS(null)} size="sm">
        <DialogTitle>Vincular Obligado Solidario</DialogTitle>
        <DialogBody>
          {(() => {
            const ca = clientAfianzadoras.find((c) => c.id === showVincularOS);
            const alreadyLinked = new Set(ca?.obligadosSolidarios || []);
            const options = obligadosSolidarios
              .filter((os) => !alreadyLinked.has(os.id))
              .map((os) => ({ value: os.id, label: os.nombre }));
            if (options.length === 0) {
              return <Text className="text-zinc-500 text-sm">No hay obligados solidarios disponibles para vincular. Crea uno primero en la seccion de abajo.</Text>;
            }
            return (
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">Selecciona un OS</label>
                <SearchableListbox
                  options={options}
                  value={selectedOSToLink}
                  onChange={setSelectedOSToLink}
                  placeholder="Seleccionar..."
                  searchPlaceholder="Buscar OS..."
                  aria-label="Buscar OS"
                  popupClassName="w-full"
                />
              </div>
            );
          })()}
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setShowVincularOS(null)}>Cancelar</Button>
          <Button color="surelia" onClick={() => handleVincularOS(showVincularOS)} disabled={!selectedOSToLink}>
            Vincular
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vincular BI Dialog */}
      <Dialog open={!!showVincularBI} onClose={() => setShowVincularBI(null)} size="sm">
        <DialogTitle>Vincular Bien Inmueble</DialogTitle>
        <DialogBody>
          {(() => {
            const ca = clientAfianzadoras.find((c) => c.id === showVincularBI);
            const alreadyLinked = new Set(ca?.bienesInmuebles || []);
            // Build options from all bienes (local + cross-client) using composite refs
            const allBiEntries = Object.entries(biMap);
            const options = allBiEntries
              .filter(([ref]) => !alreadyLinked.has(ref))
              .map(([ref, bi]) => {
                const isCross = bi._ownerClientId !== clientId;
                const crossLabel = isCross ? ` [${clientsById[bi._ownerClientId]?.nombreEmpresa || clientsById[bi._ownerClientId]?.nombre || 'Otro cliente'}]` : '';
                return { value: ref, label: `${bi.descripcion} (${bi.ownerNombre || 'Cliente'})${crossLabel}` };
              });
            if (options.length === 0) {
              return <Text className="text-zinc-500 text-sm">No hay bienes inmuebles disponibles para vincular. Crea uno primero en la seccion de abajo.</Text>;
            }
            return (
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">Selecciona un bien inmueble</label>
                <SearchableListbox
                  options={options}
                  value={selectedBIToLink}
                  onChange={setSelectedBIToLink}
                  placeholder="Seleccionar..."
                  searchPlaceholder="Buscar bien..."
                  aria-label="Buscar bien inmueble"
                  popupClassName="w-full"
                />
              </div>
            );
          })()}
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setShowVincularBI(null)}>Cancelar</Button>
          <Button color="surelia" onClick={() => handleVincularBI(showVincularBI)} disabled={!selectedBIToLink}>
            Vincular
          </Button>
        </DialogActions>
      </Dialog>

      {/* OS Dialog (create/view) */}
      <ObligadoSolidarioDialog
        open={showOSDialog}
        onClose={() => setShowOSDialog(false)}
        clientId={clientId}
        osData={osDialogData}
        onCreated={() => {}}
      />

      {/* BI Dialog (create/view) */}
      <BienInmuebleDialog
        open={showBIDialog}
        onClose={() => setShowBIDialog(false)}
        clientId={biDialogData?._ownerClientId || clientId}
        clientData={clientData}
        obligadosSolidarios={obligadosSolidarios}
        bienData={biDialogData}
        onCreated={() => {}}
        clientsById={clientsById}
      />

      {/* Delete OS Confirmation */}
      <Dialog open={!!showDeleteOSDialog} onClose={() => setShowDeleteOSDialog(null)} size="xs">
        <DialogTitle>Eliminar Obligado Solidario</DialogTitle>
        <DialogBody>
          <Text className="text-zinc-600">
            ¿Estas seguro? Se eliminara este obligado solidario y se desvinculara de todas las afianzadoras.
          </Text>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setShowDeleteOSDialog(null)}>Cancelar</Button>
          <Button color="red" onClick={() => handleDeleteOS(showDeleteOSDialog)} disabled={deletingEntity}>
            {deletingEntity ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete BI Confirmation */}
      <Dialog open={!!showDeleteBIDialog} onClose={() => setShowDeleteBIDialog(null)} size="xs">
        <DialogTitle>Eliminar Bien Inmueble</DialogTitle>
        <DialogBody>
          {(() => {
            const bi = bienesInmuebles.find((b) => b.id === showDeleteBIDialog);
            const crossRefs = bi?.crossClientRefs || [];
            const crossClientNames = crossRefs
              .map((id) => clientsById[id]?.nombreEmpresa || clientsById[id]?.nombre || id)
              .filter(Boolean);
            return (
              <>
                <Text className="text-zinc-600">
                  ¿Estas seguro? Se eliminara este bien inmueble y se desvinculara de todas las afianzadoras.
                </Text>
                {crossClientNames.length > 0 && (
                  <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <Text className="text-amber-800 text-sm font-medium">
                      Este bien esta vinculado en otros clientes:
                    </Text>
                    <ul className="mt-1 list-disc list-inside">
                      {crossClientNames.map((name, i) => (
                        <li key={i} className="text-amber-700 text-sm">{name}</li>
                      ))}
                    </ul>
                    <Text className="text-amber-600 text-xs mt-1">
                      Al eliminar, las referencias en esos clientes dejaran de funcionar.
                    </Text>
                  </div>
                )}
              </>
            );
          })()}
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setShowDeleteBIDialog(null)}>Cancelar</Button>
          <Button color="red" onClick={() => handleDeleteBI(showDeleteBIDialog)} disabled={deletingEntity}>
            {deletingEntity ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* FianzaView Modal via Portal */}
      {selectedFianza && ReactDOM.createPortal(
        <FianzaView
          isOpen={isFianzaViewOpen}
          onClose={() => { setIsFianzaViewOpen(false); setSelectedFianza(null); }}
          fianza={selectedFianza}
          clientData={clientData}
          contratoData={clientContracts.find((c) => c.id === selectedFianza.contratoId)}
          onUpdate={() => {}}
        />,
        document.body
      )}

      {/* ProcessedFileView for extracted afianzadora document data */}
      {isProcessedViewOpen && ReactDOM.createPortal(
        <ProcessedFileView
          isOpen={isProcessedViewOpen}
          onClose={() => { setIsProcessedViewOpen(false); setProcessedViewData({}); }}
          processedFileData={processedViewData.processedFileData}
          fileType={processedViewData.fileType}
          fileName={processedViewData.fileName}
          fileLabel={processedViewData.fileLabel}
          pdfUrl={processedViewData.pdfUrl}
          bypassReview
          clientId={clientId}
          clientName={clientData?.nombreEmpresa}
          clientRfc={clientData?.rfc}
        />,
        document.body
      )}
    </div>
  );
};

export default ClientAfianzadoras;
